import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useSpring } from 'framer-motion';
import { 
  FaGraduationCap, FaBriefcase, FaTools, FaEnvelope, FaPhone, 
  FaMapMarkerAlt, FaUser, FaCalendar, FaDownload, FaFilePdf, 
  FaCode, FaPaintBrush, FaPenNib, FaBullhorn, FaDesktop, 
  FaBrain, FaChevronDown, FaExternalLinkAlt, FaLaptopCode,
  FaRocket, FaLightbulb, FaCogs, FaDatabase, FaChartLine,
  FaPalette, FaStar, FaHeart
} from 'react-icons/fa';

const Home = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [activeSection, setActiveSection] = useState('hero');

  // Mouse move effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Scroll tracking
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['hero', 'education', 'experience', 'skills', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Personal Information
  const personalInfo = {
    name: "Muhammad shahzad",
    dob: "14 August 1997",
    phone: "+92 317 3597774",
    email: "Connectshahzadon@gmail.com",
    address: "Orangi town ",
    languages: ["English", "Urdu", "Sindhi"],
    status: "Software Engineering",
   
  };

  // Education
  const education = [
    {
      year: "2017",
      title: "Matriculation",
      institution: "D.S.F School",
      details: "Secondary School Certificate with focus on Science",
      type: "secondary",
      icon: <FaGraduationCap />
    },
    {
      year: "2020",
      title: "Diploma in Mechanical Engineering",
      institution: "Niams National",
      details: "Specialized in mechanical systems design & automation",
      type: "diploma",
      icon: <FaCogs />
    },
    {
      year: "2024 - Present",
      title: "High Diploma in Software Engineering",
      institution: "Aptech (ACCP AI)",
      details: "Expected completion: 2027 | Classes: Tue, Thu, Sat at 7 PM",
      type: "current",
      icon: <FaLaptopCode />
    }
  ];

  // Experience
  const experiences = [
    {
      period: "2016 - 2020",
      company: "Garments Industry",
      role: "Operator → Supervisor → Quality Controller",
      description: "Specialized in tailoring and manufacturing pants with expertise in quality control and team supervision.",
      achievements: ["Expertise in garment manufacturing", "Quality control management", "Team supervision", "Production optimization"],
      icon: <FaTools />
    },
    {
      period: "2021 - 2022",
      company: "Agricultural Warehouse",
      role: "Warehouse Manager",
      description: "Managed complete potato and onion operations from procurement to distribution.",
      achievements: ["Complete inventory management", "Supply chain operations", "Logistics coordination", "Agricultural product handling"],
      icon: <FaDatabase />
    },
    {
      period: "2023 - 2024",
      company: "Confectionery Business",
      role: "Salesman → Manufacturer → Distributor",
      description: "Managed end-to-end operations including production, sales, distribution, and customer relations.",
      achievements: ["End-to-end business operations", "Sales and distribution", "Customer relationship management", "Inventory control"],
      icon: <FaChartLine />
    }
  ];

  // Skills
  const skills = {
    design: [
      { name: "UI/UX Design", tools: "Figma, Adobe XD", level: 25 },
      { name: "Graphic Design", tools: "Canva, Adobe Suite", level: 40 },
      { name: "Video Editing", tools: "Capcut, Vsdc", level: 40 }
    ],
    development: [
      { name: "Frontend Development", tools: "HTML, CSS, JavaScript (Basic)", level: 80 },
      { name: "React Development", tools: "React.js", level: 20 },
      { name: "CSS Frameworks", tools: "Tailwind, Bootstrap", level: 90 }
    ],
    content: [
      { name: "Content Creation", tools: "Blogs, Articles", level: 95 },
      { name: "AI Integration", tools: "OpenAI, DeepSeek", level: 85 },
      { name: "Digital Marketing", tools: "Email Marketing, SEO", level: 82 }
    ]
  };

  // Tech Stack Icons (using text icons for simplicity)
  const techIcons = [
    "⚛️", "🚀", "💻", "🎨", "📱", "🌐", "🔧", "📊", 
    "🤖", "☁️", "🔗", "🎯", "✨", "⚡", "💾", "📈"
  ];

  // Handle CV Download
  const handleDownloadCV = () => {
    const link = document.createElement('a');
    link.href = '/CV.pdf';
    link.download = 'Muhammad shahzad_Professional_CV.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Scroll to section
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Floating elements component
  const FloatingElements = () => {
    const [elements] = useState(() => 
      Array.from({ length: 20 }, (_, i) => ({
        id: i,
        icon: techIcons[Math.floor(Math.random() * techIcons.length)],
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 20 + 10,
        speed: Math.random() * 20 + 10,
        delay: Math.random() * 5
      }))
    );

    return (
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        {elements.map((element) => (
          <motion.div
            key={element.id}
            className="absolute"
            style={{
              left: `${element.x}%`,
              top: `${element.y}%`,
              fontSize: `${element.size}px`,
              opacity: 0.3
            }}
            animate={{
              y: [0, -100, 0],
              x: [0, Math.random() * 50 - 25, 0],
              rotate: [0, 360]
            }}
            transition={{
              duration: element.speed,
              repeat: Infinity,
              delay: element.delay
            }}
          >
            {element.icon}
          </motion.div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white text-gray-900 relative overflow-hidden">
      {/* Animated Background */}
      <FloatingElements />
      
      {/* Mouse Tracker */}
      <div 
        className="fixed w-64 h-64 rounded-full blur-3xl opacity-10 pointer-events-none z-0 transition-all duration-300"
        style={{
          left: `${mousePosition.x}%`,
          top: `${mousePosition.y}%`,
          transform: 'translate(-50%, -50%)',
          background: 'radial-gradient(circle, #70944e 0%, #ab3737 50%, transparent 70%)'
        }}
      />

      {/* Scroll Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-olive-500 via-mehroon-500 to-olive-500 z-50 origin-left"
        style={{ scaleX }}
      />

      {/* Navigation Dots */}
      <div className="fixed right-8 top-1/2 transform -translate-y-1/2 z-40 hidden lg:block">
        <div className="flex flex-col gap-4">
          {['hero', 'education', 'experience', 'skills', 'contact'].map((section) => (
            <button
              key={section}
              onClick={() => scrollToSection(section)}
              className="relative group"
            >
              <div className={`w-3 h-3 rounded-full transition-all ${
                activeSection === section 
                  ? 'bg-gradient-to-r from-olive-500 to-mehroon-500 scale-125' 
                  : 'bg-gray-300 hover:bg-gray-400'
              }`} />
              <div className="absolute right-6 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="text-xs font-medium bg-gray-800 text-white px-2 py-1 rounded">
                  {section.charAt(0).toUpperCase() + section.slice(1)}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section id="hero" className="min-h-screen flex items-center justify-center px-4 md:px-8">
          <div className="max-w-6xl w-full text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Avatar */}
              <motion.div
                className="inline-block mb-8"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <div className="w-32 h-32 rounded-full bg-gradient-to-r from-olive-500 to-mehroon-500 mx-auto flex items-center justify-center mb-4 overflow-hidden">
  <img
    src="https://lh3.googleusercontent.com/a/ACg8ocIe4m_pPIwnWOfkrwxMSOMwXBlzp2AWZ2OK05WGITcGkWbMYo4=s400-c"
    alt="Profile"
    className="w-full h-full object-cover"
  />
</div>

              </motion.div>

              <h1 className="text-5xl md:text-7xl font-bold mb-6">
                <span className="bg-gradient-to-r from-olive-600 to-mehroon-600 bg-clip-text text-transparent">
                  {personalInfo.name}
                </span>
              </h1>

              <p className="text-2xl text-gray-600 mb-8">
                {personalInfo.status}
              </p>

              <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-12">
                {personalInfo.tagline}
              </p>

              <div className="flex flex-wrap justify-center gap-6 mb-16">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleDownloadCV}
                  className="px-8 py-4 bg-gradient-to-r from-olive-500 to-mehroon-500 text-white rounded-xl font-semibold flex items-center gap-3 shadow-lg"
                >
                  <FaDownload />
                  Download CV
                  <FaFilePdf />
                </motion.button>
                
                <button
                  onClick={() => scrollToSection('contact')}
                  className="px-8 py-4 border-2 border-olive-500 text-olive-600 rounded-xl font-semibold hover:bg-olive-50"
                >
                  Contact Me
                </button>
              </div>

              {/* Scroll Indicator */}
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="cursor-pointer"
                onClick={() => scrollToSection('education')}
              >
                <div className="flex flex-col items-center gap-2">
                  <span className="text-sm text-gray-500">Scroll to explore</span>
                  <div className="w-6 h-10 border-2 border-olive-500 rounded-full flex justify-center">
                    <div className="w-1 h-3 bg-olive-500 rounded-full mt-2" />
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Education Section */}
        <section id="education" className="py-20 px-4 md:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="bg-gradient-to-r from-olive-600 to-mehroon-600 bg-clip-text text-transparent">
                  Education Journey
                </span>
              </h2>
              <p className="text-xl text-gray-600">Integrating the discipline of traditional roots with the power of cutting edge technology to engineer modern digital solutions</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {education.map((edu, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200"
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className={`p-4 rounded-xl text-2xl ${
                      edu.type === 'current' ? 'bg-gradient-to-r from-olive-500 to-mehroon-500 text-white' :
                      edu.type === 'diploma' ? 'bg-mehroon-100 text-mehroon-600' :
                      'bg-olive-100 text-olive-600'
                    }`}>
                      {edu.icon}
                    </div>
                    <span className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full font-semibold">
                      {edu.year}
                    </span>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{edu.title}</h3>
                  <p className="text-mehroon-600 font-semibold mb-4">{edu.institution}</p>
                  <p className="text-gray-600">{edu.details}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Experience Section */}
        <section id="experience" className="py-20 px-4 md:px-8 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="bg-gradient-to-r from-mehroon-600 to-olive-600 bg-clip-text text-transparent">
                  Professional Experience
                </span>
              </h2>
              <p className="text-xl text-gray-600">Leveraging diverse industry experience to build versatile and adaptable digital solutions for modern business challenges</p>
            </div>

            <div className="space-y-8">
              {experiences.map((exp, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200"
                >
                  <div className="flex items-start gap-6 mb-6">
                    <div className="p-4 rounded-xl bg-gradient-to-r from-olive-100 to-mehroon-100 text-2xl">
                      {exp.icon}
                    </div>
                    <div>
                      <span className="inline-block px-4 py-2 bg-gray-100 text-gray-700 rounded-full mb-3">
                        {exp.period}
                      </span>
                      <h3 className="text-2xl font-bold text-gray-900">{exp.role}</h3>
                      <p className="text-mehroon-600 font-semibold">{exp.company}</p>
                    </div>
                  </div>
                  
                  <p className="text-gray-700 mb-6">{exp.description}</p>
                  
                  <div className="pt-6 border-t border-gray-200">
                    <h4 className="font-bold text-gray-900 mb-4">Key Achievements</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {exp.achievements.map((achievement, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-olive-500" />
                          <span>{achievement}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Skills Section */}
        <section id="skills" className="py-20 px-4 md:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="bg-gradient-to-r from-olive-600 to-mehroon-600 bg-clip-text text-transparent">
                  Skills & Expertise
                </span>
              </h2>
              <p className="text-xl text-gray-600">Comprehensive skill set</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
              {Object.entries(skills).map(([category, skillList], catIndex) => (
                <motion.div
                  key={category}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: catIndex * 0.2 }}
                  className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200"
                >
                  <h3 className="text-2xl font-bold text-gray-900 mb-8 capitalize">
                    {category}
                  </h3>
                  
                  <div className="space-y-6">
                    {skillList.map((skill, index) => (
                      <div key={index} className="space-y-2">
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="font-semibold text-gray-900">{skill.name}</h4>
                            <p className="text-sm text-gray-500">{skill.tools}</p>
                          </div>
                          <span className="font-bold text-mehroon-600">{skill.level}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <motion.div
                            initial={{ width: 0 }}
                            whileInView={{ width: `${skill.level}%` }}
                            viewport={{ once: true }}
                            transition={{ duration: 1, delay: index * 0.1 }}
                            className="h-2 rounded-full bg-gradient-to-r from-olive-500 to-mehroon-500"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-20 px-4 md:px-8 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="bg-gradient-to-r from-mehroon-600 to-olive-600 bg-clip-text text-transparent">
                  Let's Connect
                </span>
              </h2>
              <p className="text-xl text-gray-600">Get in touch</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Personal Statement */}
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Personal Journey</h3>
                <p className="text-gray-700 mb-6">
                  “Due to financial limitations I could not secure my future on time but I am still working with dedication effort and continuous improvement to build a better path for myself.”
                </p>
                <div className="p-6 bg-gradient-to-r from-olive-50 to-mehroon-50 rounded-xl">
                  <p className="text-gray-700 italic">
                    "Using my programming skills, I can build a complete and functional website from scratch."
                  </p>
                </div>
              </div>

              {/* Contact Info */}
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-8">Contact Details</h3>
                
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-olive-100 text-olive-600">
                      <FaUser />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Full Name</p>
                      <p className="font-bold">{personalInfo.name}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-mehroon-100 text-mehroon-600">
                      <FaPhone />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="font-bold">{personalInfo.phone}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-olive-100 text-olive-600">
                      <FaEnvelope />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-bold">{personalInfo.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-mehroon-100 text-mehroon-600">
                      <FaMapMarkerAlt />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Address</p>
                      <p className="font-bold">{personalInfo.address}</p>
                    </div>
                  </div>

                  <button
                    onClick={handleDownloadCV}
                    className="w-full py-4 bg-gradient-to-r from-olive-500 to-mehroon-500 text-white rounded-xl font-bold flex items-center justify-center gap-3 mt-8"
                  >
                    <FaDownload />
                    Download Complete CV
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

       
      </div>
    </div>
  );
};

export default Home;